// NOSONAR
/* eslint-disable max-lines, max-lines-per-function, complexity, arrow-body-style */
import AgreementSignModal from '@/components/config_components/AgreementSignModal/AgreementSignModal.vue';
import ApplicationDetailsReview from '@/components/config_components/ApplicationDetailsReview/ApplicationDetailsReview.vue';
import HistoricalVerificationChecksView from '@/components/config_components/HistoricalVerificationChecksView/HistoricalVerificationChecksView.vue';
import ManualVerificationHistoryView from '@/components/config_components/ManualVerificationHistoryView/ManualVerificationHistoryView.vue';
import EntityNameEditButton from '@/components/onboarding_components/EntityNameEditButton/EntityNameEditButton.vue';
import { extract_saved_answers } from '@/services/onboarding_form_helpers';
import { first_value } from '@/services/onboarding_data_helpers';
import {
  WORKFLOW_ACTION_STATE,
  workflowActionComputed,
  workflowActionMethods
} from '@/services/onboarding_workflow_action_service';

import {
  build_case_form_sections,
  fetch_application_transition_history,
  fetch_configured_case_gate_ids,
  fetch_entity_type_verification_bindings,
  fetch_manual_verification_history,
  fetch_manual_verification_run,
  fetch_onboarding_services,
  hydrate_case_detail,
  resolve_gate_id_from_stage_label,
  resolve_runnable_application_forms_for_service,
  run_auto_verification
} from '@/services/onboarding_journey_service';
import {
  get_case_match_values,
  get_bound_services,
  get_case_entity_type_id,
  get_manual_document_status,
  get_service_field_label_candidates,
  get_verification_detail_company,
  get_verification_detail_route_name,
  normalize_label as normalize_label_helper,
  normalize_stage,
  resolve_verification_workspace_mode,
  DEFAULT_MANUAL_SECTION_TAB,
  resolve_manual_section_tab,
  VERIFICATION_VIEW_STATE
} from './manage_verification_helpers';
import { manageVerificationComputed } from './manage_verification_computed';
import { manageVerificationLoadMethods } from './manage_verification_load_methods';
import {
  build_field_run_by_question,
  decorate_review_form_section,
  get_manual_outcome_action,
  get_manual_outcome_label,
  get_manual_outcome_tone,
  get_manual_review_rule_name,
  build_manual_review_sections_from_form_sections,
  manual_run_is_terminal,
  find_submitted_answer_for_mapping,
  manual_mappings_have_onboarding_links
} from './manage_verification_manual_helpers';
import { build_historical_manual_item } from './historical_manual_context_helpers';
import { manageVerificationMethods } from './manage_verification_methods';
import {
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';

function get_first_text(values = [], fallback = '-') {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
  return value === undefined ? fallback : String(value);
}

function build_preserved_verification_query(route = {}, extra_query = {}) {
  return {
    ...(route?.query || {}),
    ...extra_query
  };
}

export default {
  name: 'OnboardingManageVerification',
  components: {
    AgreementSignModal,
    ApplicationDetailsReview,
    EntityNameEditButton,
    HistoricalVerificationChecksView,
    ManualVerificationHistoryView
  },
  data: () => ({
    ...VERIFICATION_VIEW_STATE,
    ...WORKFLOW_ACTION_STATE,
    ...tablePaginationState()
  }),
  computed: {
    ...manageVerificationComputed,
    ...workflowActionComputed
  },
  watch: {
    is_manual_page(next_value, previous_value) {
      if (next_value === previous_value) {
        return;
      }

      this.reset_route_verification_state();
      this.load_checks();
    },
    '$route.params.applicationId'() {
      this.sync_selected_case_from_route();
    },
    active_journey_filter() {
      this.reset_verification_list_state();
      this.load_checks();
    },
    manual_workspace_item: {
      handler(next_item) {
        this.sync_bank_statement_polling(next_item);
      },
      deep: true
    },
    '$route.fullPath'() {
      this.handle_verification_route_change();
    }
  },
  mounted() {
    this.previous_gate_mode = this.current_gate_mode();
    this.load_checks();
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    this.clear_bank_statement_poll_timer();
  },
  methods: {
    ...workflowActionMethods,
    ...tablePaginationMethods,
    get_verification_loader_message() {
      return this.is_manual_page
        ? 'Loading manual verification workspace...'
        : 'Loading verification workspace...';
    },
    show_verification_system_loader(message = '') {
      const loader_message = message || this.get_verification_loader_message();
      window.dispatchEvent(new CustomEvent('system-loader:show', {
        detail: loader_message
      }));
    },
    hide_verification_system_loader_after_render() {
      this.$nextTick(() => {
        window.dispatchEvent(new CustomEvent('system-loader:hide'));
      });
    },
    handle_onboarding_data_updated() {
      this.load_checks();
    },
    async handle_verification_route_change() {
      const gate_mode_changed = this.previous_gate_mode !== this.current_gate_mode();
      this.previous_gate_mode = this.current_gate_mode();

      if (gate_mode_changed) {
        this.reset_verification_list_state();
        await this.load_checks();
        return;
      }

      await this.sync_selected_case_from_route();
    },
    current_gate_mode() {
      return resolve_verification_workspace_mode(this.$route);
    },
      sync_detail_section_state() {
        if (this.is_manual_page) {
          const available_manual_tab_ids = this.manual_section_tabs.map((tab) => tab.id);
          if (this.active_manual_section !== 'manual-case-history' && !available_manual_tab_ids.includes(this.active_manual_section)) {
            this.active_manual_section = available_manual_tab_ids.includes(DEFAULT_MANUAL_SECTION_TAB)
              ? DEFAULT_MANUAL_SECTION_TAB
              : (available_manual_tab_ids[0] || DEFAULT_MANUAL_SECTION_TAB);
          }
          return;
        }

      const available_auto_tab_ids = this.auto_section_tabs.map((tab) => tab.id);
      if (this.active_auto_section !== 'auto-case-history' && !available_auto_tab_ids.includes(this.active_auto_section)) {
        this.active_auto_section = 'auto-verification-results';
      }
    },
    reset_verification_list_state() {
      this.selected_case = null;
      this.selected_case_summary = null;
      this.checks = [];
      this.reset_table_page();
    },
    get_workflow_action_case() {
      return this.selected_case_summary || null;
    },
    async on_workflow_action_success() {
      this.close_case_summary();
      await this.load_checks();
    },
    reset_route_verification_state() {
      this.checks = [];
      this.selected_case = null;
      this.workflow_transition_history = [];
      this.previous_manual_history_run = null;
      this.close_case_summary();
      this.run_feedback = '';
      this.run_feedback_tone = 'success';
      this.expanded_check_keys = [];
      this.running_auto_keys = [];
      this.running_manual_keys = [];
      this.clear_bank_statement_poll_timer();
      this.active_auto_section = 'auto-verification-results';
      this.active_manual_section = DEFAULT_MANUAL_SECTION_TAB;
      this.manual_workspace = {
        reviewer_comments: '',
        decision_status: 'approve',
        checklist_items: []
      };
      this.manual_review_modal = {
        visible: false,
        is_submitting: false,
        is_saving_field: false,
        item: null,
        run: null,
        form_sections: [],
        field_form: null,
        decision_status: 'pass',
        decision_reason: ''
      };
      this.document_preview_modal = {
        visible: false,
        title: '',
        url: '',
        type: '',
        detail: null
      };
      this.active_manual_applicant_section_id = '';
      this.expanded_manual_applicant_form_ids = [];
      this.manual_applicant_forms_expansion_initialized = false;
      this.reset_table_page();
      this.filters = this.filters.map((filter) => (
        filter.tone === 'all'
          ? { ...filter, count: '0' }
          : filter
      ));
    },
    configured_manual_workspace_checklist() {
      const manual_item = this.manual_workspace_item || {};
      const field_runs = this.get_manual_field_runs(manual_item);
      const field_runs_by_question = new Map(
        field_runs.map((field_run) => [Number(field_run?.section_question || 0), field_run])
      );
      const mappings = Array.isArray(manual_item?.manual_mappings)
        ? manual_item.manual_mappings
        : [];

      if (mappings.length) {
        return mappings.map((mapping) => {
          const submitted_answer = this.get_submitted_answer_for_mapping(mapping);
          const manual_question_id = Number(
            mapping?.manual_section_question
            || mapping?.verification_section_question
            || 0
          );
          const existing_run = field_runs_by_question.get(manual_question_id);
          return {
            id: String(mapping?.id || manual_question_id || mapping?.onboarding_section_question),
            section_question_id: manual_question_id || null,
            label: mapping?.manual_question_text || mapping?.verification_question_text || mapping?.onboarding_question_text || 'Verification field',
            detail: mapping?.onboarding_question_text
              ? `Review against submitted value for ${mapping.onboarding_question_text}.`
              : 'Review the configured manual verification field.',
            status: this.map_manual_field_status(existing_run?.match_status),
            comment: existing_run?.reviewer_comment || '',
            source_label: mapping?.onboarding_question_text || '',
            answer: submitted_answer?.answer || '',
            file_url: submitted_answer?.file_url || '',
            file_name: submitted_answer?.file_name || '',
            type: submitted_answer?.type || '',
            mapping
          };
        });
      }

      if (manual_item?.manual_binding) {
        return [{
          id: `manual-binding-${manual_item.manual_binding.id || manual_item.verification_type || 'review'}`,
          label: get_manual_review_rule_name(manual_item),
          detail: 'Configured manual verification rule. Open the review to capture field-level findings.',
          status: '',
          comment: ''
        }];
      }

      if (manual_item?.verification_type || manual_item?.check) {
        return [{
          id: `manual-check-${manual_item.run_id || manual_item.verification_type || 'review'}`,
          label: manual_item.check || 'Manual verification',
          detail: manual_item.impact || 'Configured manual verification rule.',
          status: '',
          comment: ''
        }];
      }

      return [];
    },
    manual_workspace_checklist_items() {
      const workspace_items = Array.isArray(this.manual_workspace?.checklist_items)
        ? this.manual_workspace.checklist_items
        : [];
      return workspace_items.length ? workspace_items : this.configured_manual_workspace_checklist();
    },
    get_submitted_answer_for_mapping(mapping = {}) {
      const form_sections = this.get_manual_applicant_form_sections();
      const documents = Array.isArray(this.selected_case?.documents) ? this.selected_case.documents : [];
      return find_submitted_answer_for_mapping(form_sections, mapping, this.normalize_label, documents);
    },
    map_manual_field_status(match_status = '') {
      const normalized = String(match_status || '').toLowerCase();
      if (['match', 'matched', 'pass', 'verified'].includes(normalized)) return 'pass';
      if (['mismatch', 'failed', 'fail'].includes(normalized)) return 'fail';
      if (['not_applicable', 'unable_to_verify', 'unable to verify', 'na', 'n/a'].includes(normalized)) return 'na';
      return '';
    },
    manual_decision_action_from_status(status = '') {
      return get_manual_outcome_action({ status });
    },
    current_manual_decision_action() {
      return this.manual_workspace.decision_status
        || this.manual_decision_action_from_status(
          this.manual_workspace_item?.manual_run?.status
          || this.manual_workspace_item?.status
        );
    },
    manual_decision_label() {
      const action = this.current_manual_decision_action();
      if (action === 'request_changes') return 'Request Changes';
      if (action === 'reject') return 'Rejected';
      if (action === 'approve') return 'Approved';
      return '';
    },
    manual_decision_tone() {
      const action = this.current_manual_decision_action();
      if (action === 'request_changes') return 'changes';
      if (action === 'reject') return 'reject';
      if (action === 'approve') return 'approve';
      return '';
    },
    initialize_manual_workspace() {
      const existing_reason = String(this.manual_workspace_item?.manual_run?.decision_reason || '').trim();
      const existing_decision = this.manual_decision_action_from_status(
        this.manual_workspace_item?.manual_run?.status
        || this.manual_workspace_item?.status
      );
      this.manual_workspace = {
        reviewer_comments: existing_reason,
        decision_status: existing_decision || '',
        checklist_items: this.configured_manual_workspace_checklist()
      };
      this.active_manual_section = DEFAULT_MANUAL_SECTION_TAB;
      this.sync_active_manual_applicant_section();
      this.sync_expanded_manual_applicant_forms();
      this.sync_detail_section_state();
    },
    get_selected_case_form_sections() {
      return build_case_form_sections(this.selected_case || {});
    },
    get_manual_applicant_form_sections() {
      return build_case_form_sections(this.selected_case || {})
        .filter((form_section) => Array.isArray(form_section?.sections) && form_section.sections.length);
    },
      set_active_manual_section(section_id = '') {
        const normalized_section_id = String(section_id || '').trim();
        const available_manual_tab_ids = this.manual_section_tabs.map((tab) => tab.id);
        if (normalized_section_id === 'manual-case-history') {
          this.active_manual_section = 'manual-case-history';
          return;
        }
        this.active_manual_section = available_manual_tab_ids.includes(normalized_section_id)
          ? normalized_section_id
          : resolve_manual_section_tab(normalized_section_id);
      },
    set_active_auto_section(section_id = '') {
      const normalized_section_id = String(section_id || '').trim();
      this.active_auto_section = normalized_section_id || 'auto-verification-results';
    },
    set_active_manual_shell_tab(tab_id = 'action') {
      if (tab_id === 'reference') {
        this.set_active_manual_section('manual-applicant-information');
        return;
      }

      if (tab_id === 'history') {
        this.set_active_manual_section(this.manual_history_only_tabs[0]?.id || 'manual-case-history');
        return;
      }

      this.set_active_manual_section(DEFAULT_MANUAL_SECTION_TAB);
    },
    set_active_auto_shell_tab(tab_id = 'action') {
      if (tab_id === 'reference') {
        this.set_active_auto_section('auto-applicant-details');
        return;
      }

      if (tab_id === 'history') {
        this.set_active_auto_section(this.auto_history_only_tabs[0]?.id || 'auto-case-history');
        return;
      }

      this.set_active_auto_section('auto-verification-results');
    },
    open_gate_journey_step(step = {}) {
      if (step?.state !== 'completed' || !step?.detail_route_name || !this.selected_case) {
        return;
      }

      const application_id = Number(this.selected_case?.application_id || 0);
      if (!application_id) {
        return;
      }

      this.show_verification_system_loader(`Opening ${String(step?.label || 'gate').toLowerCase()} detail...`);
      this.$router.push({
        name: step.detail_route_name,
        params: {
          applicationId: String(application_id)
        },
        query: build_preserved_verification_query(this.$route, {
          name: this.selected_case?.name || '',
          company: get_verification_detail_company(this.selected_case)
        })
      });
    },
    async load_previous_manual_history_run(case_item = {}) {
      this.previous_manual_history_run = null;

      const entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
      if (!entity_profile_id) {
        return;
      }

      try {
        const history_rows = await fetch_manual_verification_history(entity_profile_id);
        const latest_manual_row = (Array.isArray(history_rows) ? history_rows : [])
          .filter((row) => String(row?.mode || '').trim().toLowerCase() === 'manual')
          .sort((left, right) => Number(right?.run_id || 0) - Number(left?.run_id || 0))[0] || null;

        if (!latest_manual_row?.run_id) {
          return;
        }

        this.previous_manual_history_run = await fetch_manual_verification_run(latest_manual_row.run_id);
      } catch {
        this.previous_manual_history_run = null;
      }
    },
    active_manual_history_gate_id() {
      if (this.active_historical_gate_tab?.gate_id === 'manual-verification') {
        return 'manual-verification';
      }

      if (this.previous_gate_tab?.gate_id === 'manual-verification') {
        return 'manual-verification';
      }

      return '';
    },
    previous_manual_history_item() {
        if (this.active_manual_history_gate_id() !== 'manual-verification') {
          return null;
        }

        if (this.previous_manual_history_run) {
          return {
            manual_run: this.previous_manual_history_run,
            field_runs: Array.isArray(this.previous_manual_history_run?.field_runs) ? this.previous_manual_history_run.field_runs : [],
            manual_binding: {
              manual_verification_form_name: this.previous_manual_history_run?.manual_verification_form_name || 'Manual verification'
            },
            manual_form_sections: [],
            manual_mappings: [],
            last_run: this.previous_manual_history_run?.completed_at || this.previous_manual_history_run?.started_at || ''
          };
        }

        return build_historical_manual_item(
          this.selected_case_all_checks.filter((item) => item?.verification_mode === 'manual')
        ) || this.manual_workspace_item || null;
      },
    previous_gate_transition_log() {
      const gate_id = String(
        this.active_manual_history_gate_id()
        || this.active_historical_gate_tab?.gate_id
        || this.previous_gate_tab?.gate_id
        || ''
      ).trim().toLowerCase();
      if (!gate_id) return null;

      return [...(Array.isArray(this.workflow_transition_history) ? this.workflow_transition_history : [])]
        .filter((log) => resolve_gate_id_from_stage_label(log?.to_status?.name || '') === gate_id)
        .sort((left, right) => new Date(right?.date_created || 0).getTime() - new Date(left?.date_created || 0).getTime())[0] || null;
    },
    previous_gate_history_action() {
      return get_first_text([
        this.previous_gate_transition_log()?.action_taken,
        this.previous_gate_transition_log()?.to_status?.name
      ], 'Completed');
    },
    previous_manual_history_status() {
      const manual_item = this.previous_manual_history_item();
      return get_first_text([
        this.previous_gate_transition_log()?.to_status?.name,
        get_manual_outcome_label(manual_item),
        manual_item?.manual_run?.status,
        manual_item?.status
      ]);
    },
    previous_manual_history_status_tone() {
      const manual_item = this.previous_manual_history_item();
      return first_value([
        get_manual_outcome_tone(manual_item),
        manual_item?.manual_run?.status_tone,
        manual_item?.status_tone
      ], 'secondary');
    },
    previous_manual_history_reviewer() {
      const manual_item = this.previous_manual_history_item();
      return get_first_text([
        this.previous_gate_transition_log()?.reviewer,
        this.previous_gate_transition_log()?.role,
        manual_item?.manual_run?.reviewer_name,
        manual_item?.manual_run?.reviewer,
        manual_item?.raw_status?.reviewer_name,
        manual_item?.raw_status?.reviewer,
        manual_item?.raw_status?.updated_by_name
      ], 'Reviewed');
    },
    previous_manual_history_timestamp() {
      const manual_item = this.previous_manual_history_item();
      return get_first_text([
        this.previous_gate_transition_log()?.date_created,
        manual_item?.last_run,
        manual_item?.manual_run?.completed_at,
        manual_item?.manual_run?.started_at
      ]);
    },
    previous_manual_history_comment() {
      const manual_item = this.previous_manual_history_item();
      return get_first_text([
        this.previous_gate_transition_log()?.note,
        manual_item?.manual_run?.decision_reason,
        manual_item?.raw_status?.decision_reason,
        manual_item?.raw_status?.reviewer_comment
      ], 'No reviewer comments were recorded for this gate.');
    },
    previous_manual_history_field_runs() {
      const manual_item = this.previous_manual_history_item();
      return manual_item ? this.get_manual_field_runs(manual_item) : [];
    },
    previous_manual_decision_action() {
      return get_manual_outcome_action(this.previous_manual_history_item());
    },
    previous_manual_decision_label() {
      const action = this.previous_manual_decision_action();
      if (action === 'approve') return 'Approved';
      if (action === 'request_changes') return 'Changes requested';
      if (action === 'reject') return 'Rejected';
      return this.previous_manual_history_status();
    },
    previous_manual_decision_tone() {
      const action = this.previous_manual_decision_action();
      if (action === 'approve') return 'success';
      if (action === 'request_changes') return 'warning';
      if (action === 'reject') return 'danger';
      return 'secondary';
    },
    previous_manual_review_sections() {
      const manual_item = this.previous_manual_history_item();
      if (!manual_item) return [];

      const manual_form_sections = Array.isArray(manual_item?.manual_form_sections)
        ? manual_item.manual_form_sections
        : [];
      const manual_mappings = Array.isArray(manual_item?.manual_mappings)
        ? manual_item.manual_mappings
        : [];
      const source_sections = manual_mappings_have_onboarding_links(manual_mappings)
        ? this.build_manual_review_sections_from_mappings(manual_item)
        : manual_form_sections.length
        ? build_manual_review_sections_from_form_sections(
          manual_item,
          manual_form_sections,
          manual_mappings,
          (mapping) => this.get_submitted_answer_for_mapping(mapping)
        )
        : this.build_manual_review_sections_from_mappings(manual_item);
      const field_run_by_question = build_field_run_by_question(this.previous_manual_history_field_runs());
      return source_sections
        .map((form_section) => decorate_review_form_section(form_section, field_run_by_question))
        .filter((form_section) => Array.isArray(form_section?.sections) && form_section.sections.length);
    },
    manual_applicant_section_id(form_section = {}, section = {}) {
      return [
        'manual-applicant-section',
        form_section?.id || form_section?.form_name || 'form',
        section?.id || section?.label || 'section'
      ].join('-');
    },
    manual_applicant_form_id(form_section = {}) {
      return `manual-applicant-form-${form_section?.id || form_section?.form_name || 'form'}`;
    },
    get_manual_applicant_answer_counts(section = {}) {
      const questions = (Array.isArray(section?.sub_sections) ? section.sub_sections : [])
        .flatMap((sub_section) => (Array.isArray(sub_section?.questions) ? sub_section.questions : []));
      const answered = questions.filter((question) => String(question?.answer || '').trim()).length;
      const counts = {
        answered,
        total: questions.length
      };
      return counts;
    },
    manual_applicant_section_status(section = {}) {
      const counts = this.get_manual_applicant_answer_counts(section);
      if (!counts.total) return 'Not Started';
      if (counts.answered >= counts.total) return 'Completed';
      if (counts.answered > 0) return 'In Progress';
      return 'Not Started';
    },
    manual_applicant_section_status_tone(section = {}) {
      const status = this.manual_applicant_section_status(section);
      if (status === 'Completed') return 'success';
      if (status === 'In Progress') return 'warning';
      return 'secondary';
    },
    sync_active_manual_applicant_section() {
      const form_sections = this.get_manual_applicant_form_sections();
      const first_form = form_sections.at(0);
      const first_section = first_form?.sections?.at(0);
      this.active_manual_applicant_section_id = first_section
        ? this.manual_applicant_section_id(first_form, first_section)
        : '';
    },
    sync_expanded_manual_applicant_forms() {
      this.expanded_manual_applicant_form_ids = this.get_manual_applicant_form_sections()
        .map((form_section) => this.manual_applicant_form_id(form_section));
      this.manual_applicant_forms_expansion_initialized = true;
    },
    is_manual_applicant_form_expanded(form_section = {}) {
      const form_id = this.manual_applicant_form_id(form_section);
      if (!this.manual_applicant_forms_expansion_initialized) {
        this.sync_expanded_manual_applicant_forms();
      }
      return this.expanded_manual_applicant_form_ids.includes(form_id);
    },
    toggle_manual_applicant_form(form_section = {}) {
      const form_id = this.manual_applicant_form_id(form_section);
      if (this.expanded_manual_applicant_form_ids.includes(form_id)) {
        this.expanded_manual_applicant_form_ids = this.expanded_manual_applicant_form_ids
          .filter((item) => item !== form_id);
        return;
      }
      this.expanded_manual_applicant_form_ids = [...this.expanded_manual_applicant_form_ids, form_id];
    },
    set_active_manual_applicant_section(form_section = {}, section = {}) {
      this.active_manual_applicant_section_id = this.manual_applicant_section_id(form_section, section);
      if (!this.is_manual_applicant_form_expanded(form_section)) {
        this.toggle_manual_applicant_form(form_section);
      }
    },
    is_active_manual_applicant_section(form_section = {}, section = {}) {
      const section_id = this.manual_applicant_section_id(form_section, section);
      if (!this.active_manual_applicant_section_id) {
        this.sync_active_manual_applicant_section();
      }
      return this.active_manual_applicant_section_id === section_id;
    },
    summary_tab_id_for_section(form, section) {
      return `summary-section-${form?.id || form?.form_name || 'form'}-${section?.id || section?.label || 'item'}`;
    },
    build_summary_detail_tabs(form_sections = []) {
      const document_tabs = [];
      const section_tabs = [];

      form_sections.forEach((form) => {
        (Array.isArray(form?.sections) ? form.sections : []).forEach((section) => {
          const section_label = String(section?.label || '').toLowerCase();
          const form_name = String(form?.form_name || '').toLowerCase();
          const tab = {
            id: this.summary_tab_id_for_section(form, section),
            label: section?.label || form?.form_name || 'Section',
            icon: 'bi-list-check',
            section
          };

          if (['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => section_label.includes(word) || form_name.includes(word))) {
            tab.icon = 'bi-folder';
            document_tabs.push(tab);
            return;
          }

          section_tabs.push(tab);
        });
      });

      return [
        { id: 'summary', label: 'Summary', icon: 'bi-grid' },
        ...document_tabs,
        ...section_tabs
      ];
    },
    matches_case(left_case = {}, right_case = {}) {
      const left_values = get_case_match_values(left_case);
      const right_values = get_case_match_values(right_case);
      return left_values.some((left_value) => {
        if (left_value <= 0) return false;
        return right_values.some((right_value) => right_value > 0 && left_value === right_value);
      });
    },
    build_auto_run_key(check_item = {}) {
      return [
        check_item?.case_item?.application_id || check_item?.case_item?.id || 'case',
        check_item?.application_form_id || check_item?.lead_form_id || 'form',
        check_item?.service_id || 'service'
      ].join(':');
    },
    build_manual_run_key(check_item = {}) {
      return [
        check_item?.case_item?.application_id || check_item?.case_item?.id || check_item?.case_item?.entity_profile_id || 'case',
        check_item?.verification_type || 'manual'
      ].join(':');
    },
    get_case_forms_for_check(check_item = {}) {
      const selected_case_detailed_forms = Array.isArray(this.selected_case?.detailed_forms) ? this.selected_case.detailed_forms : [];
      if (selected_case_detailed_forms.length) {
        return selected_case_detailed_forms;
      }

      const selected_case_forms = Array.isArray(this.selected_case?.forms) ? this.selected_case.forms : [];
      if (selected_case_forms.length) {
        return selected_case_forms;
      }

      const case_detailed_forms = Array.isArray(check_item?.case_item?.detailed_forms) ? check_item.case_item.detailed_forms : [];
      if (case_detailed_forms.length) {
        return case_detailed_forms;
      }

      const case_forms = Array.isArray(check_item?.case_item?.forms) ? check_item.case_item.forms : [];
      return case_forms;
    },
    get_application_form_id(form = {}) {
      return Number(form?.application_form_id || form?.id || 0);
    },
    get_form_saved_answers(form = {}) {
      return extract_saved_answers(form?.application_form || form);
    },
    normalize_label(value) {
      return normalize_label_helper(value);
    },
    form_matches_service_fields(form = {}, service = {}) {
      const label_candidates = get_service_field_label_candidates(service);
      if (!label_candidates.length) {
        return false;
      }

      const saved_answers = this.get_form_saved_answers(form);
      if (!saved_answers.length) {
        return false;
      }

      return label_candidates.some((candidate) => {
        const normalized_candidate = this.normalize_label(candidate);

        return saved_answers.some((answer) => {
          const question_label = this.normalize_label(answer?.question_label || '');
          const answer_text = String(answer?.answer_text || '').trim();

          return question_label.includes(normalized_candidate) && Boolean(answer_text);
        });
      });
    },
    async get_auto_run_candidate_form_ids(check_item = {}) {
      const current_form_id = Number(check_item?.application_form_id || 0);
      const case_forms = this.get_case_forms_for_check(check_item);

      const mapped_forms = await resolve_runnable_application_forms_for_service({
        forms: case_forms,
        service_id: check_item?.service_id
      }).catch(() => []);

      const heuristic_forms = case_forms.filter((form) => this.form_matches_service_fields(form, check_item?.service));
      const source_forms = mapped_forms.length ? mapped_forms : heuristic_forms;

      if (!source_forms.length) {
        if (current_form_id > 0) {
          return [current_form_id];
        }

        return [...new Set(
          case_forms
            .map((form) => this.get_application_form_id(form))
            .filter((form_id) => form_id > 0)
        )];
      }

      const candidate_form_ids = source_forms
        .map((form) => this.get_application_form_id(form))
        .filter((form_id) => form_id > 0);

      const unique_form_ids = [...new Set(candidate_form_ids)];
      if (!current_form_id) {
        return unique_form_ids;
      }

      if (!unique_form_ids.includes(current_form_id)) {
        return unique_form_ids;
      }

      return [
        current_form_id,
        ...unique_form_ids.filter((form_id) => form_id !== current_form_id)
      ];
    },
    is_missing_required_field_error(error) {
      const detail = String(
        error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || ''
      ).toLowerCase();

      return detail.includes('missing required') && detail.includes('field');
    },
    async run_auto_verification_with_fallback(check_item = {}) {
      const form_ids_to_try = await this.get_auto_run_candidate_form_ids(check_item);
      let last_error = null;

      for (let index = 0; index < form_ids_to_try.length; index += 1) {
        const application_form_id = form_ids_to_try.at(index);

        try {
          const response = await run_auto_verification({
            service: check_item?.service,
            service_id: check_item?.service_id,
            application_form_id,
            lead_form_id: check_item?.lead_form_id
          });

          return {
            response,
            resolved_application_form_id: application_form_id,
            used_fallback: index > 0
          };
        } catch (error) {
          last_error = error;

          const should_try_next_form = (
            index < form_ids_to_try.length - 1
            && this.is_missing_required_field_error(error)
          );

          if (!should_try_next_form) {
            throw error;
          }
        }
      }

      throw last_error;
    },
    can_run_auto_check(item = {}) {
      if (this.is_entity_verification_read_only) {
        return false;
      }

      return item?.verification_mode === 'auto'
        && item?.is_runnable !== false
        && Number(item?.service_id || 0) > 0
        && (
          Number(item?.application_form_id || item?.lead_form_id || 0) > 0
          || this.get_case_forms_for_check(item).length > 0
        );
    },
    is_manual_check(item = {}) {
      return item?.verification_mode === 'manual';
    },
    is_manual_check_running(item = {}) {
      return this.running_manual_keys.includes(this.build_manual_run_key(item));
    },
    set_manual_check_running(check_item, is_running) {
      const target_key = this.build_manual_run_key(check_item);

      if (is_running) {
        if (!this.running_manual_keys.includes(target_key)) {
          this.running_manual_keys = [...this.running_manual_keys, target_key];
        }
        return;
      }

      this.running_manual_keys = this.running_manual_keys.filter((key) => key !== target_key);
    },
    can_start_manual_review(item = {}) {
      if (this.is_entity_verification_read_only) {
        return false;
      }

      return this.is_manual_check(item) && (!Number(item?.run_id || 0) || manual_run_is_terminal(item));
    },
    can_complete_manual_review(item = {}) {
      if (this.is_entity_verification_read_only) {
        return false;
      }

      if (!this.is_manual_check(item)) {
        return false;
      }

      const normalized_status = normalize_stage(item?.status);
      return Number(item?.run_id || 0) > 0 && ['pending', 'in progress', 'partial', 'manual review'].includes(normalized_status);
    },
    is_auto_check_running(item = {}) {
      return this.running_auto_keys.includes(this.build_auto_run_key(item));
    },
    set_auto_check_running(check_item, is_running) {
      const target_key = this.build_auto_run_key(check_item);

      if (is_running) {
        if (!this.running_auto_keys.includes(target_key)) {
          this.running_auto_keys = [...this.running_auto_keys, target_key];
        }
        return;
      }

      this.running_auto_keys = this.running_auto_keys.filter((key) => key !== target_key);
    },
    set_run_feedback(message = '', tone = 'success') {
      this.run_feedback = message;
      this.run_feedback_tone = tone;
    },
    is_check_expanded(item = {}) {
      return this.expanded_check_keys.includes(String(item?.id || ''));
    },
    toggle_check_expanded(item = {}) {
      const target_key = String(item?.id || '');
      if (!target_key) {
        return;
      }

      if (this.is_check_expanded(item)) {
        this.expanded_check_keys = this.expanded_check_keys.filter((key) => key !== target_key);
        return;
      }

      this.expanded_check_keys = [...this.expanded_check_keys, target_key];
    },
    feedback_alert_class() {
      if (this.run_feedback_tone === 'danger') {
        return 'alert-danger';
      }

      if (this.run_feedback_tone === 'warning') {
        return 'alert-warning';
      }

      return 'alert-success';
    },
    apply_case_details(case_item) {
      this.selected_case = case_item;
    },
      filter_case_journey_steps(steps = [], current_gate_id = '') {
        const source_steps = Array.isArray(steps) ? steps : [];
        if (!source_steps.length) {
          return [];
        }

        const normalized_current_gate_id = String(current_gate_id || '').trim().toLowerCase();
        const current_index = source_steps.findIndex((step) => (
          String(step?.gate_id || '').trim().toLowerCase() === normalized_current_gate_id
        ));
        if (current_index < 0) {
          return source_steps;
        }

        return source_steps.map((step, index) => ({
          ...step,
          state: index < current_index ? 'completed' : index === current_index ? 'current' : 'upcoming'
        }));
      },
    async hydrate_selected_case(case_item) {
      const hydrated_case = await hydrate_case_detail(case_item, {
        include_documents: this.is_manual_page,
        include_onboarding_config: false,
        force_refresh_forms: this.is_manual_page
      }).catch(() => null);
        if (!hydrated_case) {
          this.apply_case_details(case_item);
          this.configured_gate_ids = await fetch_configured_case_gate_ids(this.selected_case).catch(() => []);
          this.workflow_transition_history = await fetch_application_transition_history(
            Number(this.selected_case?.application_id || this.selected_case?.id || 0)
          ).catch(() => []);
          await this.hydrate_verification_detail_checks(this.selected_case);
          await this.load_previous_manual_history_run(this.selected_case);
          this.initialize_manual_workspace();
          this.sync_detail_section_state();
        return;
      }

      const hydrated_entity_type_id = get_case_entity_type_id(hydrated_case, case_item);
      const hydrated_sub_entity_type_id = Number(
        hydrated_case?.sub_entity_type_id
        || hydrated_case?.profile?.sub_entity_type_id
        || 0
      ) || null;

      const [services, verification_binding_data] = await Promise.all([
        fetch_onboarding_services().catch(() => []),
        fetch_entity_type_verification_bindings(hydrated_entity_type_id, {
          sub_entity_type_id: hydrated_sub_entity_type_id
        }).catch(() => ({ entity_config_id: null, service_ids: [], manual_bindings: [] }))
      ]);
      const hydrated_bound_services = get_bound_services(verification_binding_data, services);

      this.apply_case_details({
        ...case_item,
        ...hydrated_case,
        auto_verification_services: hydrated_bound_services,
        auto_verification_entity_config_id: verification_binding_data?.entity_config_id || null,
        manual_verification_bindings: Array.isArray(verification_binding_data?.manual_bindings)
          ? verification_binding_data.manual_bindings
          : []
        });
        this.configured_gate_ids = await fetch_configured_case_gate_ids(this.selected_case).catch(() => []);
        this.workflow_transition_history = await fetch_application_transition_history(
          Number(this.selected_case?.application_id || this.selected_case?.id || 0)
        ).catch(() => []);
        await this.hydrate_verification_detail_checks(this.selected_case);
        await this.load_previous_manual_history_run(this.selected_case);
        this.initialize_manual_workspace();
      this.sync_detail_section_state();
    },
      async sync_selected_case_from_route() {
        if (!this.is_detail_page) {
          this.selected_case = null;
          this.configured_gate_ids = [];
          this.workflow_transition_history = [];
          this.is_loading_detail_case = false;
          this.hide_verification_system_loader_after_render();
          return;
      }

      this.is_loading_detail_case = true;
      const matched_row = this.case_rows.find((row) => Number(row?.case_item?.application_id || 0) === this.detail_application_id);
        if (!matched_row) {
        if (!this.is_loading_checks) this.selected_case = null;
        this.configured_gate_ids = [];
        this.workflow_transition_history = [];
        this.previous_manual_history_run = null;
        this.is_loading_detail_case = false;
        this.hide_verification_system_loader_after_render();
        return;
      }

      try {
        await this.hydrate_selected_case(matched_row.case_item);
      } finally {
        this.is_loading_detail_case = false;
        this.hide_verification_system_loader_after_render();
      }
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    open_check_detail(row_item) {
      const case_item = row_item?.case_item;
      const application_id = Number(case_item?.application_id || 0);
      if (!application_id) {
        return;
      }

      this.show_verification_system_loader('Opening verification detail...');
      this.$router.push({
        name: get_verification_detail_route_name(this.is_manual_page),
        params: {
          applicationId: String(application_id)
        },
        query: build_preserved_verification_query(this.$route, {
          name: case_item?.name || row_item?.case_name || '',
          company: get_verification_detail_company(case_item)
        })
      });
    },
    close_case_summary() {
      this.reset_workflow_action_state();
      this.selected_case_summary = null;
      this.active_summary_detail_tab = 'summary';
      this.is_summary_detail_fullscreen = false;
    },
    set_summary_detail_tab(tab_id) {
      this.active_summary_detail_tab = tab_id;
    },
    current_summary_action_tab() {
      return this.summary_detail_tabs[0] || null;
    },
    summary_reference_tab() {
      return this.summary_detail_tabs.find((tab) => {
        const label = String(`${tab?.id || ''} ${tab?.label || ''}`).trim().toLowerCase();
        return ['profile', 'personal', 'applicant', 'application'].some((word) => label.includes(word));
      }) || null;
    },
    summary_current_action_copy() {
      const label = String(this.current_summary_action_tab()?.label || 'Verification review').trim();
      return `${label} - needs your review`;
    },
    summary_reference_value() {
      const application_id = String(this.selected_case_summary?.application_id || this.selected_case_summary?.id || '').trim();
      return application_id ? `#${application_id}` : 'Open application details';
    },
    open_selected_case_profile() {
      const profile_tab = this.summary_detail_tabs.find((tab) => {
        const label = String(`${tab?.id || ''} ${tab?.label || ''}`).trim().toLowerCase();
        return label.includes('profile') || label.includes('personal') || label.includes('applicant');
      });
      this.active_summary_detail_tab = profile_tab?.id || 'summary';
    },
    toggle_summary_detail_fullscreen() {
      this.is_summary_detail_fullscreen = !this.is_summary_detail_fullscreen;
    },
    selected_case_summary_subtitle() {
      if (!this.selected_case_summary) {
        return '';
      }

      return [
        this.selected_case_summary?.detail,
        this.selected_case_summary?.activation,
        this.selected_case_summary?.eta
      ].filter(Boolean).join(' · ') || this.selected_case_summary?.stage || '';
    },
    go_back_to_verification_list() {
      this.$router.push({
        name: this.is_manual_page ? 'OnboardingManageManualVerification' : 'OnboardingManageVerification',
        query: build_preserved_verification_query(this.$route)
      });
    },
    get_manual_workspace_summary_value(candidates = [], fallback = '-') {
      const value = candidates.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
      return value === undefined ? fallback : String(value);
    },
    get_manual_document_status(document = {}) {
      return get_manual_document_status(document);
    },
    is_previewable_document(document = {}) {
      return Boolean(String(document?.file_url || '').trim());
    },
    is_previewable_manual_question(item = {}) {
      return Boolean(String(item?.file_url || '').trim());
    },
    open_manual_question_preview(item = {}) {
      this.open_document_preview({
        name: item?.file_name || item?.answer || item?.label || 'Submitted document',
        file_name: item?.file_name || item?.answer || '',
        file_url: item?.file_url || '',
        file_type: item?.type || ''
      });
    },
    preview_document_type(document = {}) {
      const file_type = String(document?.file_type || '').toLowerCase();
      const file_name = String(document?.file_name || document?.name || '').toLowerCase();

      if (file_type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(file_name)) {
        return 'image';
      }

      if (file_type === 'application/pdf' || /\.pdf$/.test(file_name)) {
        return 'pdf';
      }

      return 'file';
    },
    ...manageVerificationLoadMethods,
    ...manageVerificationMethods
  }
};


